import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import PublicIcon from '@mui/icons-material/Public';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import InsertLinkIcon from '@mui/icons-material/InsertLink';
import { Stack } from '@mui/material';
import {
    DateField,
    EditButton,
    EmailField,
    ShowButton,
    UrlField,
    useRecordContext,
} from 'react-admin';
import { AntennaSummary } from '@wwf/sdk';
import { LabelValueRow } from '@app/misc/LabelValueRow';
import { SectionDivider } from '@app/misc/SectionDivider';
import { AddressLink } from '@app/misc/AddressLink';
import { CopyAppLinkButton } from '@app/misc/CopyAppLinkButton';

interface AntennaAsideProps {
    link?: string;
}

export const AntennaAside = ({ link = 'edit' }: AntennaAsideProps) => {
    const record = useRecordContext<AntennaSummary>();
    if (!record) return null;

    return (
        <Stack ml={4}>
            <Stack direction="row" spacing={1}>
                {link === 'edit' ? (
                    <Stack
                        direction={'row'}
                        mb={2}
                        flexWrap={'wrap'}
                        justifyContent={'space-between'}
                        gap={1}
                    >
                        <EditButton resource="antenna" label="Edit Antenna" />
                        <CopyAppLinkButton type="antenna" id={record.id} />
                    </Stack>
                ) : (
                    <ShowButton resource="antenna" label="Show Antenna" />
                )}
            </Stack>

            <AntennaInfo record={record} />
            <AddressInfo record={record} />
            <AdditionalInfo record={record} />
        </Stack>
    );
};

const AntennaInfo = ({ record }: { record: AntennaSummary }) => {
    return (
        <>
            <SectionDivider title="Antenna Info" />
            <LabelValueRow
                icon={<PublicIcon color="disabled" fontSize="small" />}
                value={
                    record.website ? (
                        <UrlField
                            source="website"
                            target="_blank"
                            rel="noopener"
                            content={record.website
                                .replace('http://', '')
                                .replace('https://', '')}
                        />
                    ) : (
                        'no website'
                    )
                }
            />
            <LabelValueRow
                icon={<EmailIcon color="disabled" fontSize="small" />}
                value={
                    record.email ? <EmailField source="email" /> : 'no email'
                }
            />

            {record.phone && (
                <LabelValueRow
                    icon={<PhoneIcon color="disabled" fontSize="small" />}
                    value={record.phone}
                />
            )}

            {record.whatsappGroupVolunteers && (
                <LabelValueRow
                    icon={<InsertLinkIcon color="disabled" fontSize="small" />}
                    label="WhatsApp (volunteers)"
                    value={
                        <UrlField
                            source="whatsapp_group_volunteers"
                            target="_blank"
                            rel="noopener"
                            content={record.whatsappGroupVolunteers}
                        />
                    }
                />
            )}

            {record.whatsappGroupMainVolunteers && (
                <LabelValueRow
                    icon={<InsertLinkIcon color="disabled" fontSize="small" />}
                    label="WhatsApp (main volunteers)"
                    value={
                        <UrlField
                            source="whatsapp_group_main_volunteers"
                            target="_blank"
                            rel="noopener"
                            content={record.whatsappGroupMainVolunteers}
                        />
                    }
                />
            )}

            {record.type && <LabelValueRow label="Type" value={record.type} />}

            <LabelValueRow
                label="Departments"
                value={record.departments?.join(', ') ?? ''}
            />
        </>
    );
};

const AddressInfo = ({ record }: { record: AntennaSummary }) => {
    if (!record.address) {
        return null;
    }

    return (
        <>
            <SectionDivider title="Main Address" />
            <LabelValueRow
                icon={<LocationOnIcon color="disabled" fontSize="small" />}
                label="Street"
                value={
                    <AddressLink
                        maxWidth={250}
                        address={`${record.address.street ?? ''} ${record.address.postal_code ?? ''} ${record.address.city ?? ''}, ${record.address.country ?? ''}`}
                    />
                }
            />
            {record.address.coordinates?.latitude &&
                record.address.coordinates?.longitude && (
                    <LabelValueRow
                        icon={
                            <LocationOnIcon color="disabled" fontSize="small" />
                        }
                        label="Coordinates"
                        value={
                            <AddressLink
                                maxWidth={250}
                                address={`${record.address.coordinates.latitude} ${record.address.coordinates.longitude}`}
                            />
                        }
                    />
                )}
        </>
    );
};

const AdditionalInfo = ({ record }: { record: AntennaSummary }) => {
    if (!record.creationDate && !record.description) {
        return null;
    }

    return (
        <>
            <SectionDivider title="Additional Info" />
            <LabelValueRow
                label="Updated on"
                value={
                    <DateField
                        source="updatedAt"
                        record={record}
                        color="textPrimary"
                        options={{
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric',
                        }}
                    />
                }
            />
            <LabelValueRow
                label="Added on"
                value={
                    <DateField
                        source="createdAt"
                        record={record}
                        color="textPrimary"
                        options={{
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric',
                        }}
                    />
                }
            />
            <LabelValueRow
                label="Created on"
                value={
                    <DateField
                        source="creationDate"
                        record={record}
                        color="textPrimary"
                        options={{
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric',
                        }}
                    />
                }
            />
        </>
    );
};
export default AntennaInfo;
